/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
    OSD_MOVEMENT_TIME,
    OSD_STILL_TIME,
    PRIMARY_BRANDING_LOGO_LIVE_TV_HEIGHT_1080P,
    PRIMARY_BRANDING_LOGO_LIVE_TV_HEIGHT_720P,
    PRIMARY_BRANDING_LOGO_LIVE_TV_WIDTH_1080P,
    PRIMARY_BRANDING_LOGO_LIVE_TV_WIDTH_720P,
} from '../../../config';
import { OsdProps } from './OsdProps';
import { OsdTypes } from './OSDTypes';
import { RootState } from '../../../store/root.reducers';
import { hideOverlayDialog } from '../../../store/overlaydialog/overlaydialog.reducers';
import { ServicesClientImpl } from '../../../core/impls/ServicesClientImpl';
import { DeviceModel } from '../../../core/device/DeviceModel';
import { ImageLoader } from '../../ImageLoader';
import { ImageLoaderTypes } from '../../ImageLoader/ImageLoaderTypes';

import './Osd.scss';

const Osd: FunctionComponent<OsdProps> = (props): JSX.Element => {
    const dispatch = useDispatch();
    const { channel } = useSelector((state: RootState) => state.videoPlayer);
    const isTrickplayMenuActive = useSelector(
        (state: RootState) => state.infoBanner.isTrickplayMenuActive,
    );
    const isStandardInfoActive = useSelector(
        (state: RootState) => state.infoBanner.isStandardInfoActive,
    );
    const isFullInfoActive = useSelector((state: RootState) => state.infoBanner.isFullInfoActive);
    const visibilityState = useSelector(
        (state: RootState) => state.visibilityEvents.visibilityState,
    );
    const isOverlayDialogActive = useSelector(
        (state: RootState) => state.overlayDialog.isOverlayDialogActive,
    );
    const { message, code, type = OsdTypes.DEFAULT } = props;
    const [osdMovement, setOsdMovement] = useState({});
    const osdRef = useRef<HTMLDivElement>(null);
    const osdContainerRef = useRef<HTMLDivElement>(null);
    let interval;

    const primaryBrandingImageUrl = useRef('');

    useEffect(() => {
        if (isOverlayDialogActive) {
            dispatch(hideOverlayDialog());
        }
    }, [isOverlayDialogActive]);

    useEffect(() => {
        const service = new ServicesClientImpl();

        primaryBrandingImageUrl.current = DeviceModel.is1080P()
            ? service.getPartnerBrandingImageUrl(
                  PRIMARY_BRANDING_LOGO_LIVE_TV_WIDTH_1080P,
                  PRIMARY_BRANDING_LOGO_LIVE_TV_HEIGHT_1080P,
              )
            : service.getPartnerBrandingImageUrl(
                  PRIMARY_BRANDING_LOGO_LIVE_TV_WIDTH_720P,
                  PRIMARY_BRANDING_LOGO_LIVE_TV_HEIGHT_720P,
              );
    }, []);

    const updateOsdPosition = () => {
        if (!osdContainerRef.current || !osdRef.current) return;
        const osdContainerHeight = osdContainerRef.current.offsetHeight;
        const osdContainerWidth = osdContainerRef.current.offsetWidth;
        const osdWidth = osdRef.current.offsetWidth;
        const osdHeight = osdRef.current.offsetHeight;

        const topMovement = Math.random() * (osdContainerHeight - osdHeight);
        const leftMovement = Math.random() * (osdContainerWidth - osdWidth);
        setOsdMovement({ top: `${topMovement}px`, left: `${leftMovement}px` });
    };

    useEffect(() => {
        setOsdMovement({ top: 'auto', left: 'auto' });
        const timer = setTimeout(() => {
            interval = setInterval(updateOsdPosition, OSD_MOVEMENT_TIME);
        }, OSD_STILL_TIME);
        return () => {
            clearTimeout(timer);
            clearInterval(interval);
        };
    }, [channel, visibilityState]);

    useEffect(() => {
        setOsdMovement({ top: 'auto', left: 'auto' });
    }, [isFullInfoActive, isStandardInfoActive, isTrickplayMenuActive]);

    const osd = (
        <div className={`osd-container  ${type}`} ref={osdContainerRef} data-testid="osd-container">
            <div className="osd" style={osdMovement} ref={osdRef}>
                <ImageLoader
                    src={primaryBrandingImageUrl.current}
                    imageType={ImageLoaderTypes.OSD_BRANDING_ICON}
                    data-testid="osd-branding-icon"
                />
                <div className="osd-message" data-testid="osd-message">
                    {message}
                </div>
                {code ? (
                    <div className="osd-message-code" data-testid="osd-message-code">
                        {code}
                    </div>
                ) : null}
            </div>
        </div>
    );

    return osd;
};

Osd.propTypes = {
    message: PropTypes.string.isRequired,
    code: PropTypes.string,
    type: PropTypes.string,
};

export default Osd;
